import {Component, OnInit, OnDestroy, HostListener} from '@angular/core';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {Sidenavaction} from '../../../../../../assets/js/application/sidenavaction';
import {QPTypeService} from '../../../../../services/qpType.service';
import QPTypeModel from '../../../../../services/qpType.model';
import * as moment from 'moment';
import {ToastrService} from 'ngx-toastr';
import {CommonMethodsService} from 'src/app/services/commonMethods.service';
import {LocationStrategy} from '@angular/common';
import {PreviousRouteService} from 'src/app/services/previousRouteService';
import {BatchLoader} from "../../../../../Utils/batch-loader.util";

declare let $: any;

/**
 * QPTypeComponent
 *
 * This component is responsible for managing Quality Parameter (QP) Types within the Test Configurations module.
 * It provides a user interface for administrators to view, create, update, and search for QP types.
 * The component implements infinite scrolling for efficient data display and uses reactive forms for data entry.
 *
 * Key Responsibilities:
 * - Fetch and display a list of all QP types.
 * - Provide search functionality to filter types by name, abbreviation, or description.
 * - Manage the creation of new QP types with validation.
 * - Handle updates to existing QP types.
 * - Implement infinite scrolling using BatchLoader to handle large datasets.
 * - Manage UI state for loading indicators, form visibility, and submission status.
 *
 * Features:
 * - **CRUD Operations**: Create and Update QP types.
 * - **Real-time Search**: Filter list by multiple attributes.
 * - **Infinite Scroll**: Load data in batches to improve performance.
 * - **Form Validation**: Ensures type names follow specific patterns (alphanumeric, spaces).
 * - **Date Formatting**: Formats 'updatedAt' timestamps for display.
 *
 * Data Flow:
 * 1. Component initializes (`ngOnInit`).
 * 2. Fetches all QP types from `QPTypeService`.
 * 3. Formats dates using `moment`.
 * 4. Initializes `BatchLoader` with the full dataset.
 * 5. User interactions (scroll, search, add/edit) trigger state updates and service calls.
 * 6. `addUpdateQPType` sends data to backend and refreshes the list on success.
 *
 * @component
 * @selector app-qp-type
 * @example
 * <app-qp-type></app-qp-type>
 *
 * @implements {OnInit}
 * @implements {OnDestroy}
 * @author Generated by CompDoc
 * @version 1.0.0
 */
@Component({
  selector: 'app-qp-type',
  templateUrl: './qp-type.component.html',
  styleUrls: ['./qp-type.component.scss']
})
export class QPTypeComponent implements OnInit, OnDestroy {
  /**
   * Array holding all fetched QP types from the backend.
   * Acts as the source of truth for the list before filtering.
   * @type {QPTypeModel[]}
   * @default []
   */
  public qpTypes: QPTypeModel[] = [];

  /**
   * Array holding the currently displayed QP types.
   * This list is populated by the BatchLoader (subset of qpTypes or filteredQpTypes).
   * @type {QPTypeModel[]}
   * @default []
   */
  public qpTypesList: QPTypeModel[] = [];

  /**
   * Model for the current QP type being added or edited.
   * Bound to the form for data manipulation.
   * @type {QPTypeModel}
   */
  public qpType: QPTypeModel = new QPTypeModel();

  /**
   * Array holding QP types that match the current search criteria.
   * Used as the source for BatchLoader when filtering is active.
   * @type {QPTypeModel[]}
   * @default []
   */
  public filteredQpTypes: QPTypeModel[] = [];

  /**
   * Current search query string entered by the user.
   * @type {string}
   */
  public search: string;

  /**
   * Flag indicating if the add button was clicked (not actively used in current logic).
   * @type {boolean}
   * @default false
   */
  isAddClick = false;

  /**
   * Reactive form group for QP type details (name, abbreviation, description).
   * @type {FormGroup}
   */
  registerForm: FormGroup;

  /**
   * Flag indicating if the form has been submitted.
   * Used to trigger validation error display in the UI.
   * @type {boolean}
   * @default false
   */
  submitted = false;

  /**
   * Flag indicating if the form is in 'Create' mode.
   * @type {boolean}
   */
  public submitButton: boolean;

  /**
   * Flag indicating if the form is in 'Update' mode.
   * @type {boolean}
   */
  public updateButton: boolean;

  /**
   * Flag to prevent double clicks on submit actions.
   * @type {boolean}
   */
  public qpTypesDoubleClick: boolean;

  /**
   * Flag indicating if no data is available for display.
   * @type {boolean}
   * @default false
   */
  public NoDataMessage: boolean = false;

  /**
   * Flag to trigger clearing of the search input field.
   * @type {boolean}
   */
  public clearSearchInput: boolean;

  /**
   * Flag to control the visibility of the main page loader.
   * @type {boolean}
   * @default false
   */
  public enableDisablePageLoader: boolean = false;

  /**
   * Flag to control the visibility of the list loader spinner during infinite scroll.
   * @type {boolean}
   * @default false
   */
  public listLoader = false;              // Show listLoader spinner

  /**
   * Tracks the number of items currently loaded by the batch loader.
   * @type {number}
   * @default 0
   */
  public startIndex = 0;             // Tracks how much has been loaded

  /**
   * Flag indicating if a search filter is currently active.
   * @type {boolean}
   * @default false
   */
  public isFiltering: boolean = false;

  /**
   * Flag indicating if the list is empty (no data or no search results).
   * @type {boolean}
   * @default false
   */
  public noList: boolean = false;

  /**
   * Timeout reference for search input debouncing.
   * @type {any}
   */
  searchTimeout: any;

  /**
   * Count of fields in the form, used for layout adjustments.
   * @type {number}
   * @default 0
   */
  public formFieldCount: number = 0;

  /**
   * Flag to toggle the visibility of the add/edit form.
   * @type {boolean}
   * @default false
   */
  public showForm: boolean = false;

  /**
   * Constructor for QPTypeComponent
   *
   * Initializes the component, injects required services, and sets up the reactive form.
   *
   * Form Validation Rules:
   * - name: Required, Min 3, Max 20, Pattern (alphabetic, spaces).
   * - abbreviation: Required, Max 10, Pattern (alphanumeric).
   * - description: Max 200 characters.
   *
   * @constructor
   * @param {ToastrService} toastrService - Service for displaying toast notifications.
   * @service ToastrService
   * @param {Sidenavaction} sidenavaction - Service for side navigation actions.
   * @service Sidenavaction
   * @param {FormBuilder} formBuilder - Service for building reactive forms.
   * @service FormBuilder
   * @param {QPTypeService} qpTypeService - Service for QP Type API operations.
   * @service QPTypeService
   * @param {CommonMethodsService} commonMethodsService - Service for common utility methods and error handling.
   * @service CommonMethodsService
   * @param {LocationStrategy} locationStrategy - Service for interacting with the browser's URL.
   * @service LocationStrategy
   * @param {PreviousRouteService} previousRouteService - Service for tracking navigation history.
   * @service PreviousRouteService
   */
  constructor(private toastrService: ToastrService, public sidenavaction: Sidenavaction, public formBuilder: FormBuilder, public qpTypeService: QPTypeService, public commonMethodsService: CommonMethodsService,
              private locationStrategy: LocationStrategy, private previousRouteService: PreviousRouteService
  ) {
    this.qpType = new QPTypeModel();
    /*form validation*/
    this.registerForm = this.formBuilder.group({
      name: ['', [Validators.required, Validators.minLength(3), Validators.maxLength(20), Validators.pattern('^[a-zA-Z ]*$')]],
      abbreviation: ['', [Validators.required, Validators.maxLength(10), Validators.pattern('^[a-zA-Z0-9]*$')]],
      description: ['', [Validators.maxLength(200)]]
    });
    // Calculate field count after form is built // new
    this.formFieldCount = Object.keys(this.registerForm.controls).length;
  }

  /**
   * Utility for handling batch loading of data for infinite scroll.
   * @type {BatchLoader<any>}
   * @private
   */
  private batchLoader: BatchLoader<any>;

  /**
   * Angular lifecycle hook - Component initialization
   *
   * Performs the following initialization steps:
   * 1. Sets up side navigation.
   * 2. Resets component state and form.
   * 3. Tracks the current route.
   * 4. Fetches all QP types from the backend.
   * 5. Formats the 'updatedAt' date for each type.
   * 6. Initializes the batch loader and loads the first batch of data.
   *
   * @async
   * @method ngOnInit
   * @returns {Promise<void>}
   * @lifecycle Angular component initialization hook
   */
  public async ngOnInit() {
    this.sidenavaction.x_navigation();
    window.scrollTo(0, 0);
    this.enableDisablePageLoader = true;
    this.clear();
    this.qpTypes = [];
    this.qpTypesList = [];
    this.filteredQpTypes = [];
    this.isFiltering = false;
    this.search = '';
    this.previousRouteService.setPreviousRoute(this.locationStrategy.path());
    try {
      this.qpTypes = await this.qpTypeService.getAllQpTypes();
      if (this.qpTypes.length > 0) {
        this.qpTypes.forEach(eachQpTypes => {
          eachQpTypes.updatedAt = JSON.parse(JSON.stringify(moment(eachQpTypes.updatedAt).format('DD-MMM-YYYY hh:mm:ss A')));
        });
      }
      this.initializeBatchLoader(); // Reset instance
      this.batchLoader.reset();     // Clear state
      await this.batchLoader.loadNextBatch(true); // Load first batch
      this.enableDisablePageLoader = false;
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
      this.enableDisablePageLoader = false;
    }
  }

  /**
   * Host listener for window scroll events.
   * Triggers the check for loading the next batch of data when scrolling near the bottom.
   *
   * @method onScroll
   * @returns {void}
   */
  @HostListener('window:scroll', [])
  onScroll(): void {
    this.checkScrollAndLoad();
  }

  /**
   * Timeout reference for scroll event debouncing.
   * @type {any}
   * @private
   */
  private scrollTimeout: any;

  /**
   * Checks scroll position and loads next batch if near bottom.
   * Debounced to prevent excessive calls.
   *
   * @method checkScrollAndLoad
   * @private
   * @returns {void}
   */
  private checkScrollAndLoad(): void {
    clearTimeout(this.scrollTimeout);
    this.scrollTimeout = setTimeout(() => {
      if ((window.innerHeight + window.scrollY) >= document.body.offsetHeight - 100 && !this.batchLoader.isLoading()) {
        this.batchLoader.loadNextBatch();
      }
    }, 50);
  }

  /**
   * Initializes the BatchLoader instance.
   * Configures the source list (filtered or full) and the callback to update the displayed list.
   *
   * @method initializeBatchLoader
   * @private
   * @returns {void}
   */
  private initializeBatchLoader(): void {
    this.batchLoader = new BatchLoader<any>(
      () => this.isFiltering ? this.filteredQpTypes : this.qpTypes,
      (updatedList) => {
        this.qpTypesList = updatedList;
        this.noList = updatedList.length === 0;
      },
      undefined, // <-- omit or pass undefined
      (loading) => this.listLoader = loading
    );
  }

  /**
   * Angular lifecycle hook - Component destruction
   *
   * Cleans up resources:
   * - Cancels ongoing HTTP requests.
   * - Destroys and resets the batch loader.
   *
   * @method ngOnDestroy
   * @returns {void}
   * @lifecycle Angular component destruction hook
   */
  public ngOnDestroy(): void {
    this.qpTypeService.cancelOnGoingHttpRequests();
    this.batchLoader?.markAsDestroyed();
    this.batchLoader.reset();
  }

  /**
   * Getter for easy access to form controls in the template.
   *
   * @method qpTypeFormValidation
   * @returns {AbstractControl} Form controls
   */
  get qpTypeFormValidation() {
    return this.registerForm.controls;
  }

  /**
   * Filters the QP type list based on the search query.
   *
   * - Filters by name, abbreviation, or description (case-insensitive).
   * - Updates `filteredQpTypes`.
   * - Resets the batch loader to display filtered results.
   *
   * @async
   * @method applySearch
   * @returns {Promise<void>}
   * @side-effect Updates `filteredQpTypes`, `qpTypesList`, `noList`, `isFiltering`.
   */
  public async applySearch(): Promise<void> {
    const query = this.search?.trim().toLowerCase() || '';
    this.isFiltering = query.length > 0;
    const sourceList = this.isFiltering
      ? this.qpTypes.filter(qptype =>
        qptype.name?.toLowerCase().includes(query) ||
        qptype.abbreviation?.toLowerCase().includes(query) ||
        qptype.description?.toLowerCase().includes(query)
      )
      : this.qpTypes;
    this.filteredQpTypes = this.isFiltering ? sourceList : [];
    this.batchLoader?.reset(true);
    await this.batchLoader?.loadNextBatch(true);
    if (sourceList.length === 0) {
      //  Force noList = true if nothing to show
      this.noList = true;
      this.qpTypesList = [];
      return;
    }
    // optional: handle scroll if user already at bottom
    setTimeout(() => this.checkScrollAndLoad(), 100);
  }

  /**
   * Debounces search input changes.
   * Calls `applySearch` after a delay to prevent excessive filtering.
   *
   * @method onSearchChanged
   * @returns {void}
   */
  public onSearchChanged(): void {
    clearTimeout(this.searchTimeout);
    this.searchTimeout = setTimeout(() => {
      this.applySearch(); // debounced
    }, 300);
  }

  /**
   * Resets the form and component state to default values.
   *
   * @method clear
   * @returns {void}
   */
  public clear() {
    this.registerForm.reset();
    this.showForm = false;
    this.qpType = new QPTypeModel();
    this.clearSearchInput = false;
    this.submitButton = false;
    this.updateButton = false;
    this.qpTypesDoubleClick = false;
    this.submitted = false;
  }

  /**
   * Handles the submission of the QP Type form (Add or Update).
   *
   * - Validates the form.
   * - Calls `create` or `updateOneByID` service methods based on mode.
   * - Displays success notification.
   * - Refreshes the list via `ngOnInit`.
   *
   * @async
   * @method addUpdateQPType
   * @returns {Promise<void>}
   * @throws {Error} If the service call fails.
   */
  public async addUpdateQPType() {
    this.isAddClick = true;
    this.submitted = true;
    // stop here if form is invalid
    if (this.registerForm.invalid) {
      return;
    }
    // GET FORM VALUES
    const formData = this.registerForm.value; // contains updated values
    try {
      this.qpTypesDoubleClick = true;
      if (this.submitButton) {
        await this.qpTypeService.create(formData);
        this.toastrService.success('Qp type created successfully', '', {
          timeOut: 2000
        });
      } else {
        const updateQpType = {...formData}
        delete updateQpType.id;
        await this.qpTypeService.updateOneByID(this.qpType.id, updateQpType);
        this.toastrService.success('Qp type updated successfully', '', {
          timeOut: 2000
        });
      }
      await this.ngOnInit();
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
      this.qpTypesDoubleClick = false;
    }
  }

  /**
   * Retrieves a QP Type by ID and populates the form for editing.
   * Sets the component to 'Update' mode.
   *
   * @method getQPTypeById
   * @param {string} id - The ID of the QP type to edit.
   * @returns {void}
   */
  public getQPTypeById(id: string) {
    this.showForm = true;
    this.updateButton = true;
    this.submitButton = false;
    this.qpTypesDoubleClick = false;
    const foundQpType = this.qpTypesList.find(uom => uom?.id === id);
    this.qpType = foundQpType ? {...foundQpType} : new QPTypeModel(); // clone object
    if (this.qpType) {
      this.registerForm.patchValue({
        name: this.qpType.name,
        abbreviation: this.qpType.abbreviation,
        description: this.qpType.description
      });
    }
  }

  /**
   * Opens the form in 'Create' mode.
   *
   * @method openForm
   * @returns {void}
   */
  public openForm() {
    this.showForm = true;
    this.submitButton = true;
    this.updateButton = false;
    // Count form fields to toggle layout
    this.formFieldCount = Object.keys(this.registerForm.controls).length;
  }
}
